// 세금계산서 문서 생성 훅 (PDF/XML)

import { useState, useCallback, useRef, useEffect } from 'react';
import type { InvoiceData, Language } from '@/components/invoice/types';
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency';
import { logger } from '@/utils/logger';
import { loadJsPdf } from '@/utils/pdf';

interface UseInvoiceDocumentProps {
  invoiceData: InvoiceData;
  language: Language;
  onNotification: (message: string) => void;
}

interface UseInvoiceDocumentReturn {
  // PDF 관련
  pdfDownloading: boolean;
  pdfSuccess: boolean;
  handleDownloadPDF: () => Promise<void>;
  generatePDFBlob: () => Promise<Blob>;
  // XML 관련
  xmlDownloading: boolean;
  xmlSuccess: boolean;
  handleDownloadXML: () => Promise<void>;
  generateXMLBlob: () => Blob;
  // 공유 관련
  handleWhatsAppShare: () => void;
  handleSMSSend: () => void;
  handleCopyLink: () => void;
  handleViewOnline: () => void;
  handleEmailSend: (email: string) => Promise<void>;
}

export function useInvoiceDocument({
  invoiceData,
  language,
  onNotification
}: UseInvoiceDocumentProps): UseInvoiceDocumentReturn {
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [xmlDownloading, setXmlDownloading] = useState(false);
  const [xmlSuccess, setXmlSuccess] = useState(false);

  // 타이머 참조 (메모리 누수 방지)
  const pdfTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const xmlTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (pdfTimerRef.current) clearTimeout(pdfTimerRef.current);
      if (xmlTimerRef.current) clearTimeout(xmlTimerRef.current);
    };
  }, []);

  // 통화 포맷팅 (MXN 고정)
  const formatCurrency = useCallback((amount: number) => {
    return formatCurrencyUtil(amount, 'MXN');
  }, []);

  // 날짜/시간 포맷팅
  const formatDateTime = useCallback(
    (date: Date) => {
      return new Intl.DateTimeFormat(
        language === 'ko' ? 'ko-KR' : language === 'es' ? 'es-MX' : 'en-US',
        {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }
      ).format(date);
    },
    [language]
  );

  // PDF Blob 생성
  const generatePDFBlob = useCallback(async (): Promise<Blob> => {
    const jsPDF = await loadJsPdf();
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter'
    });

    // Header
    doc.setFillColor(3, 214, 123);
    doc.rect(0, 0, 220, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.text('COMPROBANTE FISCAL DIGITAL', 15, 15);
    doc.setFontSize(12);
    doc.text('POR INTERNET (CFDI)', 15, 22);
    doc.setFontSize(14);
    doc.text(`Folio: ${invoiceData.folio}`, 150, 15);

    doc.setTextColor(0, 0, 0);

    // Issuer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('EMISOR', 15, 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Nombre: OLULO RESTAURANT SERVICES S.A. DE C.V.', 15, 46);
    doc.text('RFC: ORS210101ABC', 15, 51);
    doc.text('Regimen Fiscal: 601 - General de Ley Personas Morales', 15, 56);
    doc.text('Lugar de Expedicion: 64000', 15, 61);

    // Recipient
    doc.setFont('helvetica', 'bold');
    doc.text('RECEPTOR', 15, 70);
    doc.setFont('helvetica', 'normal');
    doc.text(`Nombre: ${invoiceData.company.name}`, 15, 76);
    doc.text(`RFC: ${invoiceData.company.rfc}`, 15, 81);
    doc.text('Uso CFDI: G03 - Gastos en general', 15, 86);

    // Order details
    doc.setFont('helvetica', 'bold');
    doc.text('DETALLES DEL PEDIDO', 15, 95);
    doc.setFont('helvetica', 'normal');
    doc.text(`Numero de Orden: ${invoiceData.orderData.orderId}`, 15, 101);
    doc.text(`Fecha: ${formatDateTime(invoiceData.timestamp)}`, 15, 106);

    // Table
    const tableY = 115;
    doc.setFillColor(240, 240, 240);
    doc.rect(15, tableY, 180, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Concepto', 20, tableY + 5);
    doc.text('Cantidad', 120, tableY + 5);
    doc.text('Importe', 165, tableY + 5, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.text('Servicio de Restaurante', 20, tableY + 15);
    doc.text('1', 120, tableY + 15);
    doc.text(formatCurrency(invoiceData.taxDetails.beforeTax), 185, tableY + 15, { align: 'right' });

    // Totals
    const subtotalY = tableY + 25;
    doc.text('Subtotal:', 130, subtotalY);
    doc.text(formatCurrency(invoiceData.taxDetails.beforeTax), 185, subtotalY, { align: 'right' });
    doc.text(`IVA (${(invoiceData.taxDetails.taxRate * 100).toFixed(0)}%):`, 130, subtotalY + 6);
    doc.text(formatCurrency(invoiceData.taxDetails.taxAmount), 185, subtotalY + 6, { align: 'right' });
    doc.setLineWidth(0.5);
    doc.line(130, subtotalY + 10, 185, subtotalY + 10);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL:', 130, subtotalY + 19);
    doc.text(formatCurrency(invoiceData.taxDetails.total), 185, subtotalY + 19, { align: 'right' });

    doc.setFont('helvetica', 'normal');

    // UUID
    doc.setFontSize(10);
    doc.text('UUID (Folio Fiscal):', 15, subtotalY + 35);
    doc.setFontSize(8);
    doc.text(invoiceData.uuid, 15, subtotalY + 41);
    doc.setFontSize(9);
    doc.text(`Fecha de emision: ${formatDateTime(invoiceData.timestamp)}`, 15, subtotalY + 48);
    doc.text('Fecha de certificacion: ' + formatDateTime(invoiceData.timestamp), 15, subtotalY + 53);

    // QR Code placeholder
    doc.setFillColor(240, 240, 240);
    doc.rect(15, subtotalY + 60, 35, 35, 'F');
    doc.setFontSize(8);
    doc.text('QR CODE', 22, subtotalY + 80);

    // Sello Digital
    doc.setFontSize(9);
    doc.text('Sello Digital del Emisor:', 55, subtotalY + 63);
    doc.setFontSize(7);
    const selloText =
      'Este es un ejemplo de sello digital SAT. En produccion incluiria la cadena original del complemento de certificacion digital del SAT.';
    const splitSello = doc.splitTextToSize(selloText, 140);
    doc.text(splitSello, 55, subtotalY + 73);

    doc.setFontSize(9);
    doc.text('No. de Serie del Certificado del SAT:', 55, subtotalY + 90);
    doc.setFontSize(8);
    doc.text('00001000000503330328', 55, subtotalY + 95);

    // Footer
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text('Este documento es una representacion impresa de un CFDI', 105, 270, { align: 'center' });

    return doc.output('blob');
  }, [invoiceData, formatCurrency, formatDateTime]);

  // XML Blob 생성
  const generateXMLBlob = useCallback((): Blob => {
    const fechaEmision = invoiceData.timestamp.toISOString();

    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<cfdi:Comprobante xmlns:cfdi="http://www.sat.gob.mx/cfd/4"
                  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                  xsi:schemaLocation="http://www.sat.gob.mx/cfd/4 http://www.sat.gob.mx/sitio_internet/cfd/4/cfdv40.xsd"
                  Version="4.0"
                  Serie="A"
                  Folio="${invoiceData.folio}"
                  Fecha="${fechaEmision}"
                  FormaPago="99"
                  NoCertificado="00001000000503330328"
                  SubTotal="${invoiceData.taxDetails.beforeTax.toFixed(2)}"
                  Moneda="MXN"
                  Total="${invoiceData.taxDetails.total.toFixed(2)}"
                  TipoDeComprobante="I"
                  MetodoPago="PUE"
                  LugarExpedicion="64000"
                  Sello="[SELLO_DIGITAL]">

  <cfdi:Emisor Rfc="ORS210101ABC"
               Nombre="OLULO RESTAURANT SERVICES S.A. DE C.V."
               RegimenFiscal="601"/>

  <cfdi:Receptor Rfc="${invoiceData.company.rfc}"
                 Nombre="${invoiceData.company.name}"
                 UsoCFDI="G03"
                 DomicilioFiscalReceptor="64000"
                 RegimenFiscalReceptor="601"/>

  <cfdi:Conceptos>
    <cfdi:Concepto ClaveProdServ="90111500"
                   NoIdentificacion="${invoiceData.orderData.orderId}"
                   Cantidad="1"
                   ClaveUnidad="E48"
                   Unidad="Servicio"
                   Descripcion="Servicio de Restaurante"
                   ValorUnitario="${invoiceData.taxDetails.beforeTax.toFixed(2)}"
                   Importe="${invoiceData.taxDetails.beforeTax.toFixed(2)}"
                   ObjetoImp="02">
      <cfdi:Impuestos>
        <cfdi:Traslados>
          <cfdi:Traslado Base="${invoiceData.taxDetails.beforeTax.toFixed(2)}"
                         Impuesto="002"
                         TipoFactor="Tasa"
                         TasaOCuota="${invoiceData.taxDetails.taxRate.toFixed(6)}"
                         Importe="${invoiceData.taxDetails.taxAmount.toFixed(2)}"/>
        </cfdi:Traslados>
      </cfdi:Impuestos>
    </cfdi:Concepto>
  </cfdi:Conceptos>

  <cfdi:Impuestos TotalImpuestosTrasladados="${invoiceData.taxDetails.taxAmount.toFixed(2)}">
    <cfdi:Traslados>
      <cfdi:Traslado Base="${invoiceData.taxDetails.beforeTax.toFixed(2)}"
                     Impuesto="002"
                     TipoFactor="Tasa"
                     TasaOCuota="0.160000"
                     Importe="${invoiceData.taxDetails.taxAmount.toFixed(2)}"/>
    </cfdi:Traslados>
  </cfdi:Impuestos>

  <cfdi:Complemento>
    <tfd:TimbreFiscalDigital xmlns:tfd="http://www.sat.gob.mx/TimbreFiscalDigital"
                             xsi:schemaLocation="http://www.sat.gob.mx/TimbreFiscalDigital http://www.sat.gob.mx/sitio_internet/cfd/TimbreFiscalDigital/TimbreFiscalDigitalv11.xsd"
                             Version="1.1"
                             UUID="${invoiceData.uuid}"
                             FechaTimbrado="${fechaEmision}"
                             RfcProvCertif="SAT970701NN3"
                             SelloCFD="[SELLO_CFDI]"
                             NoCertificadoSAT="00001000000503330328"
                             SelloSAT="[SELLO_SAT]"/>
  </cfdi:Complemento>

</cfdi:Comprobante>`;

    return new Blob([xmlContent], { type: 'application/xml;charset=utf-8' });
  }, [invoiceData]);

  // PDF 다운로드 핸들러
  const handleDownloadPDF = useCallback(async () => {
    setPdfDownloading(true);
    setPdfSuccess(false);

    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const pdfBlob = await generatePDFBlob();
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CFDI-${invoiceData.folio}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setPdfSuccess(true);
      onNotification(
        language === 'ko' ? 'PDF 다운로드 완료' : language === 'es' ? 'PDF descargado' : 'PDF downloaded'
      );

      // 타이머 ref 사용 (메모리 누수 방지)
      if (pdfTimerRef.current) clearTimeout(pdfTimerRef.current);
      pdfTimerRef.current = setTimeout(() => {
        setPdfSuccess(false);
      }, 2000);
    } catch (error) {
      logger.error('PDF download error:', error);
      onNotification(
        language === 'ko'
          ? 'PDF 다운로드 실패'
          : language === 'es'
            ? 'Error al descargar PDF'
            : 'PDF download failed'
      );
    } finally {
      setPdfDownloading(false);
    }
  }, [generatePDFBlob, invoiceData.folio, language, onNotification]);

  // XML 다운로드 핸들러
  const handleDownloadXML = useCallback(async () => {
    setXmlDownloading(true);
    setXmlSuccess(false);

    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const xmlBlob = generateXMLBlob();
      const url = URL.createObjectURL(xmlBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CFDI-${invoiceData.folio}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setXmlSuccess(true);
      onNotification(
        language === 'ko' ? 'XML 다운로드 완료' : language === 'es' ? 'XML descargado' : 'XML downloaded'
      );

      // 타이머 ref 사용 (메모리 누수 방지)
      if (xmlTimerRef.current) clearTimeout(xmlTimerRef.current);
      xmlTimerRef.current = setTimeout(() => {
        setXmlSuccess(false);
      }, 2000);
    } catch (error) {
      logger.error('XML download error:', error);
      onNotification(
        language === 'ko'
          ? 'XML 다운로드 실패'
          : language === 'es'
            ? 'Error al descargar XML'
            : 'XML download failed'
      );
    } finally {
      setXmlDownloading(false);
    }
  }, [generateXMLBlob, invoiceData.folio, language, onNotification]);

  // WhatsApp 공유 핸들러 (민감정보 UUID 제외)
  const handleWhatsAppShare = useCallback(() => {
    // 보안: UUID는 민감정보이므로 URL에 포함하지 않음
    const message = `CFDI Generado - Folio: ${invoiceData.folio}\nTotal: ${formatCurrency(invoiceData.taxDetails.total)}\nConsulta el detalle en: https://cfdi.olulo.com/verify/${invoiceData.folio}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  }, [invoiceData.folio, invoiceData.taxDetails.total, formatCurrency]);

  // SMS 발송 핸들러 (민감정보 UUID 제외)
  const handleSMSSend = useCallback(() => {
    // 보안: UUID는 민감정보이므로 SMS에 포함하지 않음
    const message = `CFDI: ${invoiceData.folio} - Consulta: cfdi.olulo.com/verify/${invoiceData.folio}`;
    const smsUrl = `sms:?body=${encodeURIComponent(message)}`;
    window.open(smsUrl);
  }, [invoiceData.folio]);

  // 링크 복사 핸들러
  const handleCopyLink = useCallback(() => {
    const link = `https://cfdi.olulo.com/view/${invoiceData.uuid}`;
    navigator.clipboard.writeText(link);
    onNotification(
      language === 'ko' ? '링크가 복사되었습니다' : language === 'es' ? 'Enlace copiado' : 'Link copied'
    );
  }, [invoiceData.uuid, language, onNotification]);

  // 온라인 보기 핸들러
  const handleViewOnline = useCallback(() => {
    const url = `https://cfdi.olulo.com/view/${invoiceData.uuid}`;
    window.open(url, '_blank');
  }, [invoiceData.uuid]);

  // 이메일 발송 핸들러
  const handleEmailSend = useCallback(
    async (email: string) => {
      // FileReader를 Promise로 래핑하는 헬퍼 (타임아웃 포함)
      const readBlobAsBase64 = (blob: Blob, timeoutMs = 10000): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          const timeoutId = setTimeout(() => {
            reader.abort();
            reject(new Error('FileReader timeout'));
          }, timeoutMs);

          reader.onloadend = () => {
            clearTimeout(timeoutId);
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          };
          reader.onerror = () => {
            clearTimeout(timeoutId);
            const errorMessage = reader.error?.message ?? 'Unknown FileReader error';
            reject(new Error(`FileReader error: ${errorMessage}`));
          };
          reader.readAsDataURL(blob);
        });
      };

      try {
        const pdfBlob = await generatePDFBlob();
        const xmlBlob = generateXMLBlob();

        // Convert blobs to base64 for API transmission (with timeout)
        const pdfBase64 = await readBlobAsBase64(pdfBlob);
        const xmlBase64 = await readBlobAsBase64(xmlBlob);

        // Send email via API (mock implementation)
        logger.info('Sending email to:', email);
        logger.info('PDF Base64 length:', pdfBase64.length);
        logger.info('XML Base64 length:', xmlBase64.length);

        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1000));

        onNotification(
          language === 'ko'
            ? '이메일이 전송되었습니다'
            : language === 'es'
              ? 'Email enviado'
              : 'Email sent'
        );
      } catch (error) {
        logger.error('Email send error:', error);
        throw error;
      }
    },
    [generatePDFBlob, generateXMLBlob, language, onNotification]
  );

  return {
    pdfDownloading,
    pdfSuccess,
    handleDownloadPDF,
    generatePDFBlob,
    xmlDownloading,
    xmlSuccess,
    handleDownloadXML,
    generateXMLBlob,
    handleWhatsAppShare,
    handleSMSSend,
    handleCopyLink,
    handleViewOnline,
    handleEmailSend
  };
}
